import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Shield, ShieldOff, UserX, Ban, Clock, VolumeX, Volume2, Flag,
  RefreshCcw, MoreVertical, X, ChevronDown, ChevronUp,
} from "lucide-react";

/**
 * Streamer / mod control panel — shown inline on the channel page ONLY to the
 * channel owner or platform admins. Provides live viewer list + kick/ban/
 * timeout/mute/report/unban/unmute/untimeout actions.
 */
export default function StreamerModMenu({ channelUsername, canModerate }) {
  const [state, setState] = useState({ present: [], bans: [], mutes: [], timeouts: [], reports: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!canModerate) return;
    setLoading(true);
    try {
      const r = await api.get(`/channels/${channelUsername}/mod-state`);
      setState(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [channelUsername, canModerate]);

  useEffect(() => {
    if (!open) return;
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [open, load]);

  if (!canModerate) return null;

  const call = async (path, body, successMsg) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doKick = (username) =>
    call(`/channels/${channelUsername}/kick`, { username }, `Kicked @${username}`);
  const doBan = (username) => {
    const reason = prompt(`Ban @${username} from this channel — reason?`) || "";
    if (reason === null) return;
    return call(`/channels/${channelUsername}/ban`, { username, reason }, `Banned @${username}`);
  };
  const doTimeout = (username) => {
    const mins = parseInt(prompt(`Timeout @${username} for how many minutes?`, "10") || "0", 10);
    if (!mins || mins < 1) return;
    return call(`/channels/${channelUsername}/timeout`, { username, seconds: mins * 60, reason: "mod action" }, `${username} timed out for ${mins}m`);
  };
  const doMute = (username) => {
    const reason = prompt(`Mute @${username} in this channel — reason?`) || "";
    return call(`/channels/${channelUsername}/mute`, { username, reason }, `Muted @${username}`);
  };
  const doUnban = async (username) => {
    setBusy(true);
    try {
      await api.delete(`/channels/${channelUsername}/bans/${username}`);
      toast.success(`Unbanned @${username}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doUnmute = (username) => call(`/channels/${channelUsername}/unmute`, { username }, `Unmuted @${username}`);
  const doUntimeout = (username) => call(`/channels/${channelUsername}/untimeout`, { username }, `Timeout lifted for @${username}`);
  const doReport = (username) => {
    const reason = prompt(`Report @${username} to Trust & Safety — reason?`);
    if (!reason) return;
    setBusy(true);
    api.post("/reports", { target_username: username, kind: "chat_message", reason, channel_username: channelUsername })
      .then(() => toast.success("Report submitted to admins"))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setBusy(false));
  };

  const total = state.present.length + state.bans.length + state.mutes.length + state.timeouts.length;

  return (
    <div className="sc-card p-4 mt-3" data-testid="streamer-mod-menu">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2"
        data-testid="mod-menu-toggle"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[color:var(--sc-volt)]" />
          <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Streamer Mod Menu</div>
          {state.reports.length > 0 && (
            <span className="chip-neon pink text-[9px]">{state.reports.length} REPORTS</span>
          )}
          <span className="chip-neon cyan text-[9px]">{total} tracked</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Present viewers */}
          <ModSection title="Live Viewers" count={state.present.length} icon={Volume2} tone="volt">
            {state.present.length === 0 ? (
              <p className="text-xs text-zinc-500">Nobody's watching right now.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.present.map((v) => (
                  <ViewerRow
                    key={v.peer_id}
                    username={v.username}
                    busy={busy}
                    actions={{
                      kick: doKick, ban: doBan, timeout: doTimeout,
                      mute: doMute, report: doReport,
                    }}
                  />
                ))}
              </ul>
            )}
          </ModSection>

          {/* Reports about this channel */}
          <ModSection title="Open Reports" count={state.reports.length} icon={Flag} tone="pink">
            {state.reports.length === 0 ? (
              <p className="text-xs text-zinc-500">No open reports for your channel.</p>
            ) : (
              <ul className="space-y-2">
                {state.reports.map((r) => (
                  <li key={r.id} className="rounded border border-white/5 bg-black/30 p-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">@{r.reporter_username}</span>
                      <span className="text-zinc-500">→</span>
                      <span className="text-white font-bold">@{r.target_username}</span>
                      <span className="chip-neon pink text-[9px] ml-auto">{r.kind}</span>
                    </div>
                    <div className="text-zinc-300 mt-1">{r.reason}</div>
                  </li>
                ))}
              </ul>
            )}
          </ModSection>

          {/* Active timeouts */}
          <ModSection title="Timeouts" count={state.timeouts.length} icon={Clock} tone="orange">
            {state.timeouts.length === 0 ? (
              <p className="text-xs text-zinc-500">No active timeouts.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.timeouts.map((t) => (
                  <li key={t.user_id} className="flex items-center justify-between text-xs" data-testid={`timeout-row-${t.username}`}>
                    <span className="text-white">@{t.username}</span>
                    <span className="text-zinc-500 sc-mono text-[10px]">until {t.expires_at?.slice(11, 19)}</span>
                    <button onClick={() => doUntimeout(t.username)} disabled={busy} className="sc-btn-ghost text-[10px]">
                      Lift
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ModSection>

          {/* Active mutes */}
          <ModSection title="Mutes" count={state.mutes.length} icon={VolumeX} tone="cyan">
            {state.mutes.length === 0 ? (
              <p className="text-xs text-zinc-500">No muted users.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.mutes.map((m) => (
                  <li key={m.user_id} className="flex items-center justify-between text-xs" data-testid={`mute-row-${m.username}`}>
                    <span className="text-white">@{m.username}</span>
                    <span className="text-zinc-500 truncate max-w-[180px]">{m.reason}</span>
                    <button onClick={() => doUnmute(m.username)} disabled={busy} className="sc-btn-ghost text-[10px]">
                      Unmute
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ModSection>

          {/* Bans */}
          <ModSection title="Banned from Channel" count={state.bans.length} icon={Ban} tone="pink">
            {state.bans.length === 0 ? (
              <p className="text-xs text-zinc-500">No channel bans.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.bans.map((b) => (
                  <li key={b.banned_user_id} className="flex items-center justify-between text-xs" data-testid={`ban-row-${b.banned_username}`}>
                    <span className="text-white">@{b.banned_username}</span>
                    <span className="text-zinc-500 truncate max-w-[180px]">{b.reason}</span>
                    <button onClick={() => doUnban(b.banned_username)} disabled={busy} className="sc-btn-ghost text-[10px]">
                      Unban
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ModSection>

          <button
            onClick={load}
            disabled={loading}
            className="sc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="mod-menu-refresh"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      )}
    </div>
  );
}

function ModSection({ title, count, icon: Icon, tone, children }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: `var(--sc-${tone})` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: `var(--sc-${tone})` }} />
        <div className="sc-label" style={{ color: `var(--sc-${tone})` }}>{title}</div>
        <span className="text-[10px] sc-mono text-zinc-500">({count})</span>
      </div>
      {children}
    </div>
  );
}

function ViewerRow({ username, busy, actions }) {
  const [menu, setMenu] = useState(false);
  return (
    <li className="flex items-center justify-between text-xs relative" data-testid={`viewer-row-${username}`}>
      <span className="text-white truncate flex-1">@{username}</span>
      <button
        onClick={() => setMenu(!menu)}
        disabled={busy}
        className="p-1 rounded hover:bg-white/10"
        data-testid={`viewer-actions-${username}`}
        aria-label={`Actions for ${username}`}
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menu && (
        <div className="absolute right-0 top-6 z-30 sc-card p-1 min-w-[160px] shadow-2xl" onMouseLeave={() => setMenu(false)}>
          {[
            { label: "Kick from channel", icon: UserX, action: actions.kick, tone: "orange" },
            { label: "Timeout…", icon: Clock, action: actions.timeout, tone: "cyan" },
            { label: "Mute in channel", icon: VolumeX, action: actions.mute, tone: "cyan" },
            { label: "Ban from channel", icon: Ban, action: actions.ban, tone: "pink" },
            { label: "Report to admin", icon: Flag, action: actions.report, tone: "pink" },
          ].map((a) => (
            <button
              key={a.label}
              onClick={() => { setMenu(false); a.action(username); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-white/10 rounded"
              data-testid={`viewer-action-${a.label.split(" ")[0].toLowerCase()}-${username}`}
            >
              <a.icon className="w-3 h-3" style={{ color: `var(--sc-${a.tone})` }} /> {a.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
